/// <amd-module name="Release/Landing/HubContent/Deployments/ActionCreator" />
import { IVssPageContext } from "VSS/Platform/Context";

import { IDeploymentsResult } from "Release/Client/Contracts/Extended";
import { IReleaseService } from "Release/Client/Service/Release";
import { ActionCreatorBase } from "DistributedTask/Flux/Actions";
import { ActionsHubManager } from "DistributedTask/Flux/ActionsHubManager";
import { DeploymentsActions } from "./Actions";
import { DeploymentStatus, DeploymentOperationStatus, ReleaseQueryOrder, Release, ReleaseEnvironment } from "Release/Client/Contracts/Release";
import { IDeploymentsFilterState } from "../Filter/FilterTypes";
import { IReleaseHubServiceDataService } from "PageData";
import { IDeploymentsListData } from "DataProviderContracts";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { RmServiceInstanceId, MessageBarKeys } from "Constants";
import { MessageActions } from "HubMessageBar/MessageActions";

const deploymentsListDataProviderId = "ms.vss-releaseManagement-web.deployments-list-data-provider";

export class DeploymentsActionCreator extends ActionCreatorBase {
    public static getKey(): string {
        return "release-landing-deployments-action-creator";
    }

    public initialize(instanceId?: string) {
        this._actions = ActionsHubManager.GetActionsHub<DeploymentsActions>(DeploymentsActions);
        this._messageActions = ActionsHubManager.GetActionsHub<MessageActions>(MessageActions);
    }

    public getDeployments(pageContext: IVssPageContext, definitionId: number, filters: IDeploymentsFilterState): void {
        const raiseAction = (deploymentsResult: IDeploymentsListData) => {
            this._actions.setDeployments.invoke({
                continuationToken: deploymentsResult.continuationToken || 0,
                definitionId: definitionId,
                deployments: deploymentsResult.deployments,
                environments: deploymentsResult.environments || [],
                filterState: filters
            });
        }

        const dataService = pageContext.getService<IReleaseHubServiceDataService>("IReleaseHubServiceDataService");
        const deploymentsResult = dataService.getDeployments(definitionId);
        if (deploymentsResult && deploymentsResult.deployments) {
            raiseAction(deploymentsResult);
            return;
        }

        const contributionService = pageContext.getService<IVssContributionService>("IVssContributionService");
        let additionalProperties = { definitionId: definitionId, definitionEnvironmentId: filters.environmentId, branch: filters.branch, createdFor: filters.createdBy };

        contributionService.getDataAsync<IDeploymentsListData>(
            deploymentsListDataProviderId,
            additionalProperties,
            true,
            RmServiceInstanceId).then((result: IDeploymentsListData) => {
                raiseAction(result);
            },
                (error: Error) => {
                    this._messageActions.setMessage.invoke({ key: MessageBarKeys.HubError, value: error.message ? error.message : error });
                    throw error;
                }
            );
    }

    public fetchMoreDeployments(pageContext: IVssPageContext, definitionId: number, filters: IDeploymentsFilterState, continuationToken: number = 0): void {
        // 0, negative, null or undefined
        if (!continuationToken || continuationToken <= 0) {
            return;
        }

        const releaseService = pageContext.getService<IReleaseService>("IReleaseService");
        releaseService.getDeployments(
            definitionId,
            filters.environmentId,
            undefined,
            undefined,
            undefined,
            DeploymentStatus.All,
            DeploymentOperationStatus.All,
            false,
            ReleaseQueryOrder.Descending,
            25 /* Default Top */,
            continuationToken,
            filters.createdBy,
            undefined,
            undefined,
            filters.branch
        ).then((deploymentsResult: IDeploymentsResult) => {
            this._actions.deploymentsUpdatedWithMore.invoke({
                continuationToken: deploymentsResult.continuationToken || 0,
                definitionId: definitionId,
                deployments: deploymentsResult.deployments
            });
        });
    }

    public getDeploymentsForCreatedRelease(pageContext: IVssPageContext, definitionId: number, releaseId: number): void {
        const releaseService = pageContext.getService<IReleaseService>("IReleaseService");
        releaseService.getRelease(
            releaseId
        ).then((release: Release) => {
            this._actions.updateDeploymentsForCreatedRelease.invoke({
                definitionId: definitionId,
                release: release
            });
        });
    }

    public updateReleaseEnvironmentStatus(definitionId: number, environmentId: number, deploymentStatus: DeploymentStatus, operationStatus: DeploymentOperationStatus): void {
        this._actions.updateDeploymentStatus.invoke({
            definitionId: definitionId,
            environmentId: environmentId,
            deploymentsStatus: deploymentStatus,
            operationStatus: operationStatus
        });
    }

    public handleDeploymentStarted(pageContext: IVssPageContext, definitionId: number, releaseId: number, releaseEnvironmentId: number): void {
        const releaseService = pageContext.getService<IReleaseService>("IReleaseService");
        releaseService.getRelease(releaseId)
            .then((release: Release) => {
                this._actions.handleDeploymentStarted.invoke({ definitionId: definitionId, release: release, releaseEnvironmentId: releaseEnvironmentId });
            });
    }

    private _actions: DeploymentsActions;
    private _messageActions: MessageActions;
}
